import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { cloneDeep } from 'lodash';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AnalysisUpsellPopupComponent } from '../../../../Monitor/Briefings/components/AnalysisUpsellPopup/analysis-upsell-popup.component';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { BehaviorSubject, catchError, combineLatest, map, Observable, Subject, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import {
    getMonitoringDateQueryInputFolderOptions,
    getMonitoringDateQueryInputTopicOptions
} from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { ReportSeriesModel } from '../../models/report-series.model';
import { KeyMessageGroupApi } from '../../../../api/ai/model/key-message-group-api.';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { CoverageAnalysisService } from '../../../../Monitor/Shared/services/coverage-analysis.service';
import { CoverageInsights } from '../../../../Monitor/Shared/models/coverage-insights.model';
import { AiAnalysisTypes } from '../../../Mentions/enums/ai-analysis-types.enum';
import { Mention } from '../../../Mentions/models/mention';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { promptLanguageOptions } from '../../../Forms/data/prompt-language-options';
import { PromptModel } from '../../../Mentions/models/prompt.model';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { MessageService } from 'primeng/api';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { SearchStatesEnum } from '../../../../Monitor/Shared/enums/search-states.enum';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { RelativeTimePipe } from '../../../Misc/pipes/RelativeTime/relative-time.pipe';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ReportsDashboardSearchService } from '../../services/reports-dashboard-search.service';
import { MentionCardClickedEvent } from '../ChartDrillDownDialog/chart-drill-down-dialog.component';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { MentionDetailsTakeOverPanelComponent } from '../../../Mentions/components/MentionDetailsTakeOverPanel/mention-details-take-over-panel.component';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { ManageMentionOrganizationDialogComponent } from '../../../Mentions/components/ManageMentionOrganization/manage-mention-organization-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { CoverageInsightConfigModel } from '../../models/coverage-insight-config.model';

@Component({
    selector: 'agpr-coverage-insight',
    templateUrl: './coverage-insight.component.html',
    styleUrls: ['./coverage-insight.component.scss'],
    animations: [
        trigger('toggleContent', [
            state('collapsed', style({
                height: '{{height}}',
            }), { params: { height: '88px' } }),
            state('expand', style({
                height: '*',
            })),
            transition('collapsed <=> expand', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})

export class CoverageInsightComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly aiPopupWidth: number = 485;
    readonly aiPopupHeight: number = 372;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly promptLanguageOptions = promptLanguageOptions;
    readonly aiAnalysisTypes = AiAnalysisTypes;
    readonly defaultPrompts = [
        {type: AiAnalysisTypes.COVERAGE_INSIGHT_EXTENDED_ANALYSIS},
        { type: AiAnalysisTypes.COVERAGE_INSIGHT_CONTEXTUAL_ANALYSIS },
        { type: AiAnalysisTypes.COVERAGE_INSIGHT_SENTIMENT_OPINION },
        { type: AiAnalysisTypes.COVERAGE_INSIGHT_BRAND_IMPACT },
        { type: AiAnalysisTypes.COVERAGE_INSIGHT_RESEARCH_AND_BACKGROUND_LINKS },
    ];;


    @Input() drilldownSeries: ReportSeriesModel;
    @Input() set coverageInsightConfig(config: CoverageInsightConfigModel) {
        if (config) {
            this.keyMessageGroups = config.keyMessageGroups;
            this.mentions = config.mentions;
            this.skipClearState = true;
        }
    };
    @Input() set externalKeyMessageGroups(groups: KeyMessageGroupApi[]) {
        if (groups !== null && groups !== undefined) {
            this.keyMessageGroups = groups;
        }
    }
    @Input() set isActive(active: boolean) {
        if (!active) {
            return;
        }
        const selected = this.keyMessageGroups?.find(g => g.selected);
        if (selected && selected !== this.lastLoadedKeyMessage) {
            this.loadMentionsForKeyMessage(selected);
        }
    }
    @Output() keyMessageGroupsChanged = new EventEmitter<KeyMessageGroupApi[]>();
    @Output() dateChanged = new EventEmitter<DateQueryModel>();
    @Output() emitSourceChanged = new EventEmitter<ReportSeriesModel>();

    @ViewChild('mentionScrollPanel') mentionScrollPanel: ScrollPanelComponent;
    @ViewChild('keyMessageScrollPanel') keyMessageScrollPanel: ScrollPanelComponent;
    @ViewChild('promptScrollPanel') promptScrollPanel: ScrollPanelComponent;

    id: string = 'coverage_insight';
    hasCoverageInsightsEnabled = false;
    overlayContainer: Element;
    protected subscriptions: Subscription[] = [];

    dateOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();
    searchRange: SearchRangeModel = null;
    dateQuery: DateQueryModel;
    mentionCount: number = 0;
    warningMentionCount: number = 400;
    errorMentionCount: number = 10000;
    animating: boolean = false;
    userDefinedKeyMessageString: string;
    keyMessageGroups: KeyMessageGroupApi[] = [];
    collapsed: boolean = true;

    mentions: Mention[] = [];
    selectedMentionCount: number = 0;
    selectedFolder: Folder = null;
    folderList$: Observable<Folder[]>;
    saveInProgress: boolean = false;
    loading: boolean = false;
    loadingMentions: boolean = false;
    lastLoadedKeyMessage: KeyMessageGroupApi = null;
    relativeTimePipe: RelativeTimePipe;
    showBackButton: boolean = false;
    cachedSearchCriteria: MentionSearchCriteriaModel;
    skipClearState: boolean = false;
    analysisContext: string = '';

    folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Monitoring.folderPicker.accountOption',
            action: (searchString: string) => {},
            appearCondition: AppearCondition.always,
            clearOnSelect: true
        },
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder, undefined, true).pipe(
                    take(1),
                    switchMap(id => this.folderService.all$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    ))
                ).subscribe(newFolder => {
                    this.selectedFolder = newFolder;
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    selectedLanguage;
    prompts: PromptModel[] = cloneDeep(this.defaultPrompts);

    constructor(
        private injector: Injector,
        private coreDialogService: CoreDialogService,
        private monitorConfigService: MonitorConfigService,
        private folderService: FolderService,
        private mentionsService: MentionsService,
        private mentionSearchService: ReportsDashboardSearchService,
        private coverageAnalysisService: CoverageAnalysisService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private userStateService: CurrentUserStateService,
        private targetingRhpContextsService: TargetingRhpContextsService,
    ) {
        this.overlayContainer = document.getElementById('main-content');
        this.folderList$ = this.folderService.all$;
        this.setSubscriptions();
    }
    ngOnInit(): void {
        this.sourceChanged(this.drilldownSeries, false);
        this.relativeTimePipe = new RelativeTimePipe(this.localizationService);
    }

    /**
         * Sets subscriptions, pushing to array for easy cleanup later
         */
    private setSubscriptions(): void {
        this.subscriptions.push(
            this.monitorConfigService.getHasCoverageInsightsEnabled().subscribe(x => {
                this.hasCoverageInsightsEnabled = x;
            }),
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                }
            ),
            this.mentionSearchService.getDateQuery().subscribe(dateQuery => {
                this.dateQuery = dateQuery;
                this.clearState();
            }),
            this.mentionSearchService.getItemCount().subscribe(search => {
                this.mentionCount = search;
            }),
            combineLatest(
                [
                    this.mentionSearchService.getSearchState(),
                    this.mentionSearchService.getSearchCriteria()
                ]
            ).subscribe(
                ([searchState, searchCriteria]) => {
                    this.loading = searchState.type === SearchStatesEnum.loading;
                    if (this.loading && this.cachedSearchCriteria && !this.cachedSearchCriteria.equals(searchCriteria) ) {
                        this.clearState();
                    }

                    this.cachedSearchCriteria = searchCriteria?.clone();
                    this.skipClearState = false;
                }
            ),
            this.userStateService.getPreferences().subscribe(preference => {
                if (preference?.uiLanguage) {
                    if (preference.uiLanguage.languageTag === 'fr-FR') {
                        preference.uiLanguage.languageTag = 'fr';
                    }
                    this.selectedLanguage = this.promptLanguageOptions.find(x => x.value === preference.uiLanguage.languageTag);
                } else {
                    this.selectedLanguage = this.promptLanguageOptions.find(x => x.value === 'en-US');
                }
            }),
            this.userStateService.getUILanguageTag().subscribe(tag => {
                this.promptLanguageOptions.forEach(option => {
                    option.label = tag + '.' + option.value;
                });
            })
        );
    }

    analyzeCoverage(event): void {
        if (this.hasCoverageInsightsEnabled) {
            this.loading = true;
            const coverageInsights = new CoverageInsights();
            coverageInsights.aiAnalysisType = AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT;
            coverageInsights.focus = this.analysisContext;
            this.coverageAnalysisService.getKeyMessages(
                coverageInsights,
                this.mentionSearchService.getMentionSearchCriteriaPayload()
            ).pipe(
                catchError(e => {
                    this.loading = false;
                    // eslint-disable-next-line max-len
                    if (e?.error?.details?.length && e?.error?.details?.[0].invalidValue === 'Invalid request, the number of mentions that allow ai process is 0.' ) {
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary : this.localizationService.transform('Reports.coverageInsight.error.noMentionAllowed.summary'),
                            detail: this.localizationService.transform('Reports.coverageInsight.error.noMentionAllowed.detail')
                        });
                    } else {
                        this.errorMessage();
                    }

                    throw e;
                })).subscribe(keyMessages => {
                this.keyMessageGroups = keyMessages;
                this.keyMessageGroupsChanged.emit(this.keyMessageGroups);
                this.resetScrollPanel();
                if (this.keyMessageGroups?.length) {
                    // by default, selected the first key narrative
                    this.selectKeyMessage(this.keyMessageGroups[0]);
                }

                this.loading = false;
            });
        } else {
            const mousePositionX = (event as PointerEvent)?.clientX || 0;
            const mousePositionY = (event as PointerEvent)?.clientY || 0;
            const ioModel = this.coreDialogService.getIoConfigBasedOnMousePosition(mousePositionX, mousePositionY, this.aiPopupWidth, this.aiPopupHeight);
            ioModel.inputs.overlayContainer = this.overlayContainer;
            ioModel.inputs.message = 'Reports.coverageInsight.upSell.message';
            ioModel.inputs.bullets = [
                'Reports.coverageInsight.upSell.bullet1',
                'Reports.coverageInsight.upSell.bullet2',
                'Reports.coverageInsight.upSell.bullet3'
            ];
            this.coreDialogService.open(AnalysisUpsellPopupComponent, ioModel, this.injector);
        }
    }

    applyUserDefinedKeyMessage(): void {
        this.loading = true;
        const coverageInsights = new CoverageInsights();
        coverageInsights.aiAnalysisType = AiAnalysisTypes.COLLECTION_KEY_MESSAGES_USER_DEFINED;
        coverageInsights.userDefinedKeyMessage = this.userDefinedKeyMessageString;
        this.coverageAnalysisService.getUserDefinedKeyMessages(
            coverageInsights,
            this.mentionSearchService.getMentionSearchCriteriaPayload(),
            this.drilldownSeries.isSavedSource()
        ).pipe(catchError(e => {
            this.loading = false;
            this.errorMessage();

            throw e;
        })).subscribe(keyMessages => {
            const userDefinedKeyMessages = new KeyMessageGroupApi();
            userDefinedKeyMessages.keyMessageGroupTitle = this.userDefinedKeyMessageString;
            let ids = [];
            let maxExampleSize = 0;
            keyMessages?.forEach(keyMessage => {
                let examples = [];
                if (keyMessage?.examples?.length) {
                    examples = keyMessage.examples.filter(x => !!x);
                    ids.push(...examples);
                }

                if (keyMessage.summary && examples.length > maxExampleSize) {
                    maxExampleSize = examples.length;
                    userDefinedKeyMessages.keyMessageGroupSummary = keyMessage.summary;
                }
            });

            ids = ids.filter((item, index) => ids.indexOf(item) === index);
            if (ids.length > 400) {
                ids = ids.slice(0, 400);
            }
            userDefinedKeyMessages.examples = ids;

            if (!ids.length) {
                userDefinedKeyMessages.keyMessageGroupSummary = this.localizationService.transform(
                    'Reports.coverageInsight.userDefinedMessage.noResult.summary', {keyMessage: this.userDefinedKeyMessageString}
                );
            }

            this.keyMessageGroups.push(userDefinedKeyMessages);
            this.keyMessageGroupsChanged.emit(this.keyMessageGroups);
            this.showBackButton = this.keyMessageGroups.length === 1 && !this.keyMessageGroups[0].examples?.length;
            this.loading = false;
            this.collapsed = true;
            this.userDefinedKeyMessageString = '';
            this.selectKeyMessage(userDefinedKeyMessages);
        });
    }

    private errorMessage() {
        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary : this.localizationService.transform('Reports.coverageInsight.error.genericSummary'),
            detail: this.localizationService.transform('Reports.coverageInsight.error.genericDetail')
        });
    }

    sourceChanged(series: ReportSeriesModel, doEmit: boolean = true) {
        this.clearState();

        if (series?.isSavedSource()) {
            this.dateOptions = getMonitoringDateQueryInputFolderOptions();
        } else {
            this.dateOptions = getMonitoringDateQueryInputTopicOptions();
            if (this.dateQuery.type === DateQueryTypeEnum.ALL) {
                this.dateQuery = this.dateOptions[0].value.query;
            }
        }

        if (doEmit) {
            this.emitSourceChanged.next(series);
        }
    }

    clearState(): void {
        if (this.skipClearState) {
            return;
        }

        this.mentionCount = 0;
        this.backToAnalysis();
    }

    backToAnalysis(): void {
        this.showBackButton = false;
        this.mentions = [];
        this.keyMessageGroups = [];
        this.keyMessageGroupsChanged.emit(this.keyMessageGroups);
        this.collapsed = true;
        this.prompts?.forEach(prompt => {
            this.cancelGeneration(prompt);
        });
        this.prompts = cloneDeep(this.defaultPrompts);

    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => {
                subscription.unsubscribe();
            }
        );
    }

    /**
     * Performs the toggle animation
     */
    toggle(event) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        if (this.animating) {
            return false;
        }

        this.animating = true;


        if (this.collapsed) {
            this.collapsed = false;
        } else {
            this.collapsed = true;
        }

    }

    onToggleDone(): void {
        this.animating = false;
    }

    toggleSelectAll(): void {
        if (this.selectedMentionCount > 0) {
            this.mentions.forEach(x => x.selected = false);
            this.selectedMentionCount = 0;
        } else {
            this.mentions.forEach(x => x.selected = true);
            this.selectedMentionCount = this.mentions.length;
        }
    }

    mentionSelectChange(): void {
        this.selectedMentionCount = this.mentions.filter(x => x.selected).length;
    }

    saveSelectedMentions(): void {
        this.saveInProgress = true;
        const selectedMentions = this.mentions.filter(x => x.selected);
        if (this.selectedFolder) {
            this.folderService.saveMentionsToFolder(
                selectedMentions,
                this.selectedFolder,
                false
            ).subscribe(() => {
                this.saveInProgress = false;
            });
        } else {
            this.mentionsService.saveMentionsToAccount(
                selectedMentions,
                false,
            ).subscribe(() => {
                this.saveInProgress = false;
            });
        }
    }

    /**
     * updates the date range and re-runs the search
     *
     * @param dateRange
     *
     * @returns void
     */
    dateRangeChanged(): void {
        this.clearState();
        this.dateChanged.next(this.dateQuery);
    }

    selectKeyMessage(keyMessageGroup: KeyMessageGroupApi): void {
        if (keyMessageGroup.selected) {
            return;
        }

        this.keyMessageGroups.forEach(x => x.selected = false);
        keyMessageGroup.selected = true;
        this.loadMentionsForKeyMessage(keyMessageGroup);
    }

    private loadMentionsForKeyMessage(keyMessageGroup: KeyMessageGroupApi): void {
        this.lastLoadedKeyMessage = keyMessageGroup;
        this.loadingMentions = true;
        this.selectedMentionCount = 0;
        this.prompts?.forEach(prompt => {
            this.cancelGeneration(prompt);
        });
        this.prompts = cloneDeep(this.defaultPrompts);
        const ids = keyMessageGroup.examples.filter(x => !!x);
        if (!ids?.length) {
            this.loadingMentions = false;
            this.mentions = [];
            return;
        }

        let mentionRequest;

        if (this.drilldownSeries.isSavedSource()) {
            mentionRequest = this.mentionsService.getMentionByIds(ids).pipe(map(res => {
                const mentions = [];
                keyMessageGroup.examples.forEach(id => {
                    if (res[id]) {
                        mentions.push(res[id]);
                    }
                });

                return mentions;
            }));
        } else {
            mentionRequest = this.mentionsService.getMentionByMercuryIds(ids);
        }
        mentionRequest.subscribe(mentions => {
            this.mentions = [];
            requestAnimationFrame(() => {
                this.mentionScrollPanel?.scrollTo();
            });
            mentions.forEach(mentionApi => {
                if (mentionApi) {
                    const mention = new Mention(mentionApi);
                    mention.relativeDateString = this.relativeTimePipe.transform(mention.dateAvailable);
                    this.mentions.push(mention);
                }
            });
            this.loadingMentions = false;
        });
    }

    generateInsight(prompt: PromptModel): void {
        const aiAnalysisType = prompt.type as AiAnalysisTypes;
        const selectedKeywordGroup = this.keyMessageGroups.find(x => x.selected);
        const coverageInsights = new CoverageInsights();
        coverageInsights.aiAnalysisType = aiAnalysisType;
        coverageInsights.selectedLanguageOption = this.selectedLanguage;
        coverageInsights.keyMessageTitle = selectedKeywordGroup.keyMessageGroupSummary;
        coverageInsights.keyMessageSummary = selectedKeywordGroup.keyMessageGroupSummary;

        switch (aiAnalysisType) {
            case AiAnalysisTypes.COVERAGE_INSIGHT_CONTEXTUAL_ANALYSIS:
                if (!prompt.context) {
                    return;
                }

                coverageInsights.context = prompt.context;
                break;
            case AiAnalysisTypes.COVERAGE_INSIGHT_SENTIMENT_OPINION:
                if (prompt.keywords?.length) {
                    coverageInsights.aiAnalysisType = AiAnalysisTypes.COVERAGE_INSIGHT_SENTIMENT_OPINION_WITH_KEYWORDS;
                    coverageInsights.keywords = prompt.keywords;
                } else {
                    coverageInsights.aiAnalysisType = AiAnalysisTypes.COVERAGE_INSIGHT_SENTIMENT_OPINION;
                }

                break;
            case AiAnalysisTypes.COVERAGE_INSIGHT_BRAND_IMPACT:
                if (!prompt.brand) {
                    return;
                }

                coverageInsights.brand = prompt.brand;
                break;
            default:
                break;
        }

        const isSavedSource = this.drilldownSeries.isSavedSource();
        prompt.generating = true;
        prompt.destroyApiCall$ = new Subject<void>();
        const mentionIds = this.mentions.map(x => isSavedSource ? x.mentionId : x.id);

        prompt.apiCall = this.coverageAnalysisService.generateCoverageInsight(
            coverageInsights,
            mentionIds,
            isSavedSource
        ).pipe(
            takeUntil(prompt.destroyApiCall$),
            catchError(e => {
                prompt.generating = false;
                prompt.destroyApiCall$ = null;
                this.errorMessage();

                throw e;
            })).subscribe(res => {
            prompt.generating = false;
            prompt.destroyApiCall$ = null;

            if (res.html) {
                prompt.insightText = res.html;
            } else {
                prompt.noContentGenerated = true;
            }
        });
    }

    cancelGeneration(prompt: PromptModel): void {
        if (prompt.destroyApiCall$) {
            prompt.destroyApiCall$.next();
        }
        if(prompt.apiCall) {
            prompt.apiCall.unsubscribe();
        }
        prompt.generating = false;
    }

    mentionCardClicked(event: MentionCardClickedEvent) {
        if (!event || !event.mention) {
            return;
        }

        this.mentionsService.setFocusedMention(event.mention);
        this.setRHPContextFromMention(event.mention);
        this.openMentionDetailsTakeOver(event.eventType);
    }

    private openMentionDetailsTakeOver(eventType: 'contact' | 'outlet' | 'mention') {
        const io: DialogIoModel = {
            inputs: {
                focusTab: eventType,
                fromReports: true
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionDetailsTakeOverPanelComponent, io, this.injector);
    }

    private setRHPContextFromMention(mention: Mention) {
        if (mention.author?.uuid) {
            this.targetingRhpContextsService.setFullProfileContactById(mention.author.uuid);
        } else {
            this.targetingRhpContextsService.clearContact();
        }
        if (mention.publication?.uuid) {
            this.targetingRhpContextsService.setFullProfileOutletById(mention.publication.uuid);
        } else {
            this.targetingRhpContextsService.clearOutlet();
        }
    }

    openManageMentionOrganizationDialog(mention: Mention) {
        this.mentionsService.setFocusedMention(mention);

        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                mention
            },
            outputs: {
                dialogClose: (newMention: Mention) => {
                    if (newMention) {
                        const {folderIds = [], tagIds = []} = newMention;
                        mention.folderIds = folderIds;
                        mention.tagIds = tagIds;
                    }
                }
            }
        });
    }

    // This might be a bug from primeng, sometimes initial load, there is a wired scrollbar, below code resolve it
    resetScrollPanel(): void {
        requestAnimationFrame(() => {
            this.keyMessageScrollPanel?.scrollTo();
            this.promptScrollPanel?.scrollTo();
            this.mentionScrollPanel?.scrollTo();
        });
    }
}

